// Single renderer — plan §6.3 + M5.5 custom input.
// Value shape: { optionId: string; customInput?: string }.
// option.allowCustomInput=true ise opsiyon seçildiğinde altında textarea.

import { useId } from "react";
import { CUSTOM_INPUT_MAX_LENGTH } from "@mahir/shared";
import type { QuestionSnapshot } from "@mahir/shared";
import { FORM_FILL_COPY } from "../../../lib/form-fill-copy";

interface Props {
  question: QuestionSnapshot;
  value: { optionId: string; customInput?: string } | null;
  onChange: (v: { optionId: string; customInput?: string } | null) => void;
}

interface OptionItem {
  id: string;
  label: string;
  allowCustomInput?: boolean;
}

export function SingleRenderer({ question, value, onChange }: Props): JSX.Element {
  const opts = (question.options as { choices?: OptionItem[] } | null)?.choices ?? [];
  const selectedId = value?.optionId ?? null;
  // M15 self-review B2 — grup adı instance-benzersiz: composite'te iki single parça
  // aynı question.code'u taşır; salt code'lu name iki radyo grubunu tek native gruba
  // düşürüp seçimleri çakıştırıyordu. useId render başına stabil, instance başına ayrık.
  const groupId = useId();

  const setOption = (id: string): void => {
    // Seçim değişince varolan customInput'u koru sadece aynı opsiyondaysa
    if (id === value?.optionId) return;
    onChange({ optionId: id });
  };

  const setCustom = (text: string): void => {
    if (!selectedId) return;
    onChange({
      optionId: selectedId,
      ...(text.length > 0 ? { customInput: text } : {}),
    });
  };

  return (
    <div className="flex flex-col gap-2">
      {opts.length === 0 ? (
        <div
          className="text-xs px-2 py-1 rounded"
          style={{ background: "var(--color-bg)", color: "var(--color-text-3)" }}
        >
          Bu soru için seçenek tanımlanmamış.
        </div>
      ) : (
        opts.map((o) => {
          const checked = selectedId === o.id;
          return (
            <div key={o.id}>
              <label
                className="flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer"
                style={{
                  background: checked ? "var(--color-bg)" : "var(--color-surface)",
                  border: "1px solid",
                  borderColor: checked ? "var(--color-primary)" : "var(--color-border)",
                }}
              >
                <input
                  type="radio"
                  name={`q-${question.code}-${groupId}`}
                  checked={checked}
                  onChange={() => setOption(o.id)}
                  style={{ accentColor: "var(--color-primary)" }}
                />
                <span
                  style={{
                    color: checked ? "var(--color-primary)" : "var(--color-text)",
                    fontSize: "0.88rem",
                    fontWeight: checked ? 600 : 400,
                  }}
                >
                  {o.label}
                </span>
                {o.allowCustomInput && (
                  <span
                    style={{
                      marginLeft: "auto",
                      fontFamily: "var(--font-mono)",
                      fontSize: "0.65rem",
                      color: "var(--color-text-3)",
                    }}
                  >
                    {FORM_FILL_COPY.customInputBadge}
                  </span>
                )}
              </label>
              {checked && o.allowCustomInput && (
                <textarea
                  value={value?.customInput ?? ""}
                  onChange={(e) => setCustom(e.target.value)}
                  maxLength={CUSTOM_INPUT_MAX_LENGTH}
                  rows={2}
                  placeholder={`${o.label} — lütfen açıklayınız…`}
                  className="mt-1 ml-8 w-full rounded-lg px-3 py-2 text-sm resize-none"
                  style={{
                    background: "var(--color-surface)",
                    border: "1px solid var(--color-border)",
                    color: "var(--color-text)",
                    fontFamily: "var(--font-sans)",
                    width: "calc(100% - 2rem)",
                  }}
                />
              )}
            </div>
          );
        })
      )}
    </div>
  );
}
